// [ai] The postMessage protocol between a sandboxed widget (sdk/client.ts → /sdk/atlas.js) and its frame (widgets/Sandbox.tsx).
// Pure: parse() is the only way a message becomes a frame, dispatch() the only way a method name reaches the host. The frame
// also pushes what the widget reads synchronously (size, theme, media state, its own state) as events on the SYNC id.
import type { AtlasEvents, AtlasHost, Params } from "./types";
export const ATLAS = "atlas/1";
export const SYNC = "*";
export type Call = { atlas: typeof ATLAS; type: "call"; id: string; method: string; args: unknown[] };
export type Ready = { atlas: typeof ATLAS; type: "ready" };
export type Escape = { atlas: typeof ATLAS; type: "escape" };
export type Sub = { atlas: typeof ATLAS; type: "sub"; id: string; event: string };
export type Unsub = { atlas: typeof ATLAS; type: "unsub"; id: string };
export type Result = { atlas: typeof ATLAS; type: "result"; id: string; ok: true; value: unknown } | { atlas: typeof ATLAS; type: "result"; id: string; ok: false; error: string };
export type Ev = { atlas: typeof ATLAS; type: "event"; id: string; args: unknown[] };
export type WidgetMsg = Call | Ready | Escape | Sub | Unsub;
export type HostMsg = Result | Ev;
export type Msg = WidgetMsg | HostMsg;
const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x);
const str = (x: unknown): x is string => typeof x === "string" && x.length > 0;

export function parse(data: unknown): Msg | null {
  if (!isObj(data) || data.atlas !== ATLAS || !str(data.type)) return null;
  const { type } = data;
  if (type === "ready" || type === "escape") return { atlas: ATLAS, type };
  if (type === "call") return str(data.id) && str(data.method) && Array.isArray(data.args) ? { atlas: ATLAS, type, id: data.id, method: data.method, args: data.args } : null;
  if (type === "sub") return str(data.id) && str(data.event) ? { atlas: ATLAS, type, id: data.id, event: data.event } : null;
  if (type === "unsub") return str(data.id) ? { atlas: ATLAS, type, id: data.id } : null;
  if (type === "result") {
    if (!str(data.id)) return null;
    if (data.ok === true) return { atlas: ATLAS, type, id: data.id, ok: true, value: data.value };
    if (data.ok === false) return { atlas: ATLAS, type, id: data.id, ok: false, error: String(data.error ?? "error") };
    return null;
  }
  if (type === "event") return str(data.id) && Array.isArray(data.args) ? { atlas: ATLAS, type, id: data.id, args: data.args } : null;
  return null;
}

/** Ids unique per maker and across makers (a random salt per maker, a counter inside). */
export function ids(prefix: string): () => string { const salt = Math.random().toString(36).slice(2, 8); let n = 0; return () => `${prefix}-${salt}-${++n}`; }
export const EVENTS: (keyof AtlasEvents)[] = ["time", "size", "theme", "focus"];
export const isEvent = (e: string): e is keyof AtlasEvents => (EVENTS as string[]).includes(e);
export const sync = (what: string, value: unknown): Ev => ({ atlas: ATLAS, type: "event", id: SYNC, args: [what, value] });
const bad = (m: string, what: string) => new Error(`${m}: ${what}`);

/** One method name + args → one call on the host. Arguments are checked here so a widget never reaches the host with a wrong shape. */
export async function dispatch(host: AtlasHost, method: string, args: unknown[]): Promise<unknown> {
  const a0 = args[0], a1 = args[1];
  switch (method) {
    case "query": if (!str(a0)) throw bad(method, "a query name is expected"); if (a1 !== undefined && !isObj(a1)) throw bad(method, "params must be an object"); return host.query(a0, (a1 as Params | undefined) ?? {});
    case "media.play": if (!isObj(a0) || !str(a0.rec)) throw bad(method, "{rec, t?, e?} is expected"); return host.media.play({ rec: a0.rec, t: typeof a0.t === "number" ? a0.t : undefined, e: typeof a0.e === "number" ? a0.e : null });
    case "media.pause": host.media.pause(); return undefined;
    case "media.seek": if (typeof a0 !== "number" || !Number.isFinite(a0)) throw bad(method, "a time in seconds is expected"); host.media.seek(a0); return undefined;
    case "media.state": return host.media.state();
    case "media.focus": host.media.focus(); return undefined;
    case "media.release": host.media.release(); return undefined;
    case "open": if (!str(a0)) throw bad(method, "an href is expected"); host.open(a0); return undefined;
    case "state.get": if (!str(a0)) throw bad(method, "a key is expected"); return host.state.get(a0);
    case "state.set": if (!str(a0)) throw bad(method, "a key is expected"); host.state.set(a0, a1); return undefined;
    case "theme": return host.theme();
    case "ask": if (!str(a0)) throw bad(method, "a prompt is expected"); host.ask(a0); return undefined;
    case "size": return host.size();
    default: throw new Error(`unknown method "${method}"`);
  }
}
